import type {
  DashboardWidget,
  WidgetQueryConfig,
} from '../../types/dashboard.types';

export const THRESHOLD_COMPARATORS = ['below', 'above'] as const;
export type ThresholdComparator = (typeof THRESHOLD_COMPARATORS)[number];
export const DEFAULT_THRESHOLD_COMPARATOR: ThresholdComparator = 'below';
import { ChecklistItemStatus } from '../../../checklist/types/ChecklistItemStatus.enum';
import type { Breakdown } from './widget-builder.component';
import {
  CANONICAL_METRIC_TYPES,
  MetricType,
  QueryLevel,
  REPEATABLE_MODES,
  RepeatableMode,
  TASK_REMARKS_FIELD_ID,
  isFieldBreakdown,
  pivotPairFromGroupBy,
} from './query-config.util';

export type StoredWidget = Pick<
  DashboardWidget,
  'widgetType' | 'queryConfig' | 'chartConfig' | 'title'
> &
  Partial<Pick<DashboardWidget, 'position'>>;

export interface WidgetFormDraft {
  widgetType: DashboardWidget['widgetType'];
  templateLineageId: string | null;
  level: QueryLevel;
  sourceSectionId: string | null;
  sourceItemId: string | null;
  sourceCustomInputId: string | null;
  sourceItemIds: string[];
  sourceCustomInputIds: string[];
  metric: MetricType | null;
  metrics: MetricType[];
  repeatableMode: RepeatableMode | null;
  breakdown: Breakdown;
  statuses: ChecklistItemStatus[];
  title: string;
  unitLabel: string;
  thresholdValue: string;
  thresholdComparator: ThresholdComparator;
}

function isRemarksList(queryConfig: WidgetQueryConfig): boolean {
  return queryConfig.metric === 'item_remarks_list';
}

export function inferLevel(queryConfig: WidgetQueryConfig): QueryLevel {
  if (isRemarksList(queryConfig)) return 'custom_input';
  if (queryConfig.sourceCustomInputId) return 'custom_input';
  if (
    queryConfig.sourceCustomInputIds &&
    queryConfig.sourceCustomInputIds.length > 0
  ) {
    return 'custom_input';
  }
  if (queryConfig.sourceItemId) return 'item';
  if (queryConfig.sourceItemIds && queryConfig.sourceItemIds.length > 0)
    return 'item';
  return 'checklist';
}

export function narrowToUnion<T extends string>(
  value: string | null,
  list: readonly T[]
): T | null {
  return (list as readonly string[]).includes(value as string)
    ? (value as T)
    : null;
}

function breakdownFromGroupBy(groupBy: string[] | null | undefined): Breakdown {
  if (pivotPairFromGroupBy(groupBy)) return 'pivot';
  if (isFieldBreakdown(groupBy)) return 'field';
  if (groupBy?.length === 1 && groupBy[0] === 'item_status')
    return 'item_status';
  if (groupBy?.length === 1 && groupBy[0] === 'time') return 'time';
  if (groupBy?.length === 1 && groupBy[0] === 'answer_value')
    return 'answer_value';
  return 'total';
}

function narrowMetricsList(
  values: readonly string[] | null | undefined
): MetricType[] {
  return (values ?? [])
    .map(value => narrowToUnion(value, CANONICAL_METRIC_TYPES))
    .filter((metric): metric is MetricType => metric !== null);
}

export function widgetConfigToFormDraft(widget: StoredWidget): WidgetFormDraft {
  const { queryConfig, chartConfig } = widget;
  const unitLabel = chartConfig?.['unitLabel'];
  const thresholdValue = chartConfig?.['thresholdValue'];
  const thresholdComparator = chartConfig?.['thresholdComparator'];

  const metrics =
    queryConfig.metrics && queryConfig.metrics.length > 0
      ? narrowMetricsList(queryConfig.metrics)
      : (() => {
          const single = narrowToUnion(
            queryConfig.metric,
            CANONICAL_METRIC_TYPES
          );
          return single ? [single] : [];
        })();

  return {
    widgetType: widget.widgetType,
    templateLineageId: queryConfig.templateLineageId,
    level: inferLevel(queryConfig),
    sourceSectionId: queryConfig.sourceSectionId ?? null,
    sourceItemId: queryConfig.sourceItemId ?? null,
    sourceCustomInputId: isRemarksList(queryConfig)
      ? TASK_REMARKS_FIELD_ID
      : (queryConfig.sourceCustomInputId ?? null),
    sourceItemIds: queryConfig.sourceItemIds ?? [],
    sourceCustomInputIds: queryConfig.sourceCustomInputIds ?? [],
    metric: metrics[0] ?? null,
    metrics,
    repeatableMode: narrowToUnion(
      queryConfig.repeatableMode ?? null,
      REPEATABLE_MODES
    ),
    breakdown: breakdownFromGroupBy(queryConfig.groupBy),
    statuses: queryConfig.filters?.statuses ?? [],
    title: widget.title,
    unitLabel: typeof unitLabel === 'string' ? unitLabel : '',
    thresholdValue:
      typeof thresholdValue === 'number' && Number.isFinite(thresholdValue)
        ? String(thresholdValue)
        : '',
    thresholdComparator:
      thresholdComparator === 'above' ? 'above' : DEFAULT_THRESHOLD_COMPARATOR,
  };
}

export function parseThresholdInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^-?\d*[.,]?\d*$/.test(trimmed)) return null;
  const parsed = Number.parseFloat(trimmed.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

export const BUILDER_OWNED_CHART_CONFIG_KEYS = [
  'unitLabel',
  'thresholdValue',
  'thresholdComparator',
] as const;

export function mergeChartConfig(
  previous: Record<string, unknown> | null | undefined,
  builderOutput: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  const prev = previous ?? {};
  const output = builderOutput ?? {};
  const ownedKeys: readonly string[] = BUILDER_OWNED_CHART_CONFIG_KEYS;
  const result: Record<string, unknown> = {};

  for (const key of Object.keys(prev)) {
    if (!ownedKeys.includes(key)) {
      result[key] = prev[key];
    }
  }

  for (const key of BUILDER_OWNED_CHART_CONFIG_KEYS) {
    if (key in output) {
      result[key] = output[key];
    }
  }

  return Object.keys(result).length > 0 ? result : null;
}
